import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emailRecipientsApi } from '@/api/services/emailRecipients';
import type {
  CreateEmailRecipientDto,
  SetEmailRecipientGroupModeDto,
  UpdateEmailRecipientDto,
} from '@/api/types/emailRecipients';

export const emailRecipientKeys = {
  all: ['emailRecipients'] as const,
  list: ['emailRecipients', 'list'] as const,
  groupSettings: ['emailRecipients', 'groupSettings'] as const,
};

export function useGroupSettings() {
  return useQuery({
    queryKey: emailRecipientKeys.groupSettings,
    queryFn: () => emailRecipientsApi.getGroupSettings(),
  });
}

export function useCreateRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEmailRecipientDto) => emailRecipientsApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailRecipientKeys.list }),
  });
}

export function useUpdateRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEmailRecipientDto }) =>
      emailRecipientsApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailRecipientKeys.list }),
  });
}

export function useDeleteRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => emailRecipientsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailRecipientKeys.list }),
  });
}

export function useSetGroupMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetEmailRecipientGroupModeDto) => emailRecipientsApi.setGroupMode(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailRecipientKeys.groupSettings }),
  });
}
